import React, { useEffect, useMemo, useRef } from 'react'
import {
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
} from '@mui/material'

import PipelineView from './PipelineView'

import { useOutletContext } from 'react-router-dom'
import WizardContainer from '../shared/WizardContainer'
import WizardFooter from '../shared/WizardFooter'
import usePipelines from '../../hooks/api/usePipelines'
import useAlert from '../../hooks/useAlert'

const useFirstRender = () => {
  const ref = useRef(true)
  const firstRender = ref.current
  ref.current = false
  return firstRender
}

const PipelineSetup = () => {
  const {
    navigateToNext,
    companyConfig,
    updateCompanyConfig,
    wizardState,
    updateWizardState,
  } = useOutletContext()

  const { pipelines = [] } = wizardState

  const hasInitialilzedData = useMemo(() => !!wizardState.pipelines, [])
  const isFirstRender = useFirstRender()

  const {
    pipelines: pipelinesData,

    selectedPipelineId,
    setPipelines,

    updatePipeline,
    updateStage,
    moveStage,
    removeStage,
    removePipeline,
    addStage,
    selectPipeline,
    savePipelines,

    isLoading,
    hasLoadError,
  } = usePipelines()

  useEffect(() => {
    if (isFirstRender) {
      if (hasInitialilzedData) {
        return setPipelines(pipelines)
      }
      return
    }

    updateWizardState({ pipelines: pipelinesData })
  }, [pipelinesData])

  const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId)

  const skip = () => {
    navigateToNext()
  }

  const { createAlert } = useAlert()
  const submit = () => {
    const newConfig = {
      ...companyConfig,
      hasSavedPipelines: true,
    }

    updateCompanyConfig(newConfig)
      .then(savePipelines)
      .then(() => {
        navigateToNext()
      })
      .catch(() => {
        createAlert({
          severity: 'error',
          message: 'Something went wrong',
          action: {
            label: 'Skip this step',
            onClick: () => navigateToNext(),
          },
        })
      })
  }

  if (isLoading) return <CircularProgress />

  return (
    <WizardContainer>
      <Typography variant={'h5'}>Pipelines</Typography>

      <Typography sx={{ mb: 2 }}>
        Pipelines are used to keep track of where your customer is in the
        lifecycle of your relationship with them. Every step from your initial
        contact with them, to wrapping up a deal will be tracked using these
        pipelines. You can set up multiple pipelines, each with its own stages,
        to track these processes at the same time. Let's get started with
        choosing and setting up your pipelines.
      </Typography>

      <TextField
        margin='dense'
        fullWidth
        label={'Select a pipeline'}
        value={selectedPipelineId}
        onChange={selectPipeline}
        select
        sx={{ mb: 2 }}
      >
        {pipelines.map((p, i) => (
          <MenuItem key={i} value={p.id}>
            {p.name}
          </MenuItem>
        ))}
      </TextField>

      {selectedPipeline && (
        <PipelineView
          pipeline={selectedPipeline}
          moveStage={moveStage}
          removeStage={removeStage}
          updatePipeline={updatePipeline}
          addStage={addStage}
          updateStage={updateStage}
          removePipeline={removePipeline}
        />
      )}

      <WizardFooter
        skipButton={{
          onClick: skip,
          label: 'Skip and use defaults',
        }}
        primaryButton={{
          onClick: submit,
          label: 'Save and Continue',
        }}
      />
    </WizardContainer>
  )
}

export default PipelineSetup
